import { titles } from "@/mocks/titles";
import { news } from "@/mocks/news";
import type { MediaType, Title } from "@/types/media";

export const allTitles: Title[] = titles as Title[];

export const CATEGORY_META: Record<MediaType, { label: string; slug: MediaType; intro: string; seoDesc: string }> = {
  movie: {
    label: "电影",
    slug: "movie",
    intro: "汇聚全球最新院线佳作与经典高分电影，覆盖科幻、动作、悬疑、爱情等多种题材，高清在线观看。",
    seoDesc: "西瓜影音电影频道，聚合最新院线大片与经典高分电影，涵盖科幻、动作、悬疑、爱情等多种类型，支持高清在线观看。",
  },
  tv: {
    label: "电视剧",
    slug: "tv",
    intro: "精选国内外热播电视剧与口碑佳作，古装、悬疑、都市、青春一网打尽，追剧不再迷路。",
    seoDesc: "西瓜影音电视剧频道，精选国内外热播剧与口碑佳剧，涵盖古装、悬疑、都市、青春等类型，追剧更轻松。",
  },
};

export const REGIONS = ["全部", "中国大陆", "美国", "英国", "日本", "韩国", "中国香港"];
export const YEARS = ["全部", "2024", "2023", "2022", "2021"];
export const GENRES = [
  "全部", "剧情", "科幻", "动作", "喜剧", "爱情", "悬疑", "犯罪", "古装", "冒险",
  "家庭", "奇幻", "战争", "武侠", "青春", "医疗", "西部", "科技", "惊悚",
];
export const SORTS = [
  { key: "hot", label: "热门" },
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
] as const;

export type SortKey = (typeof SORTS)[number]["key"];

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  sort: SortKey;
}

export const DEFAULT_FILTER: FilterState = {
  region: "全部",
  year: "全部",
  genre: "全部",
  sort: "hot",
};

export function getTitleById(id: string): Title | undefined {
  return allTitles.find((item) => item.id === id);
}

export function getTitlesByType(type: MediaType): Title[] {
  return allTitles.filter((item) => item.type === type);
}

export function getFeatured(): Title[] {
  return allTitles.filter((item) => item.featured);
}

export function getLatest(type: MediaType | "all", limit = 6): Title[] {
  const pool = type === "all" ? allTitles : getTitlesByType(type);
  return [...pool].sort((a, b) => b.year - a.year || b.viewsNum - a.viewsNum).slice(0, limit);
}

export function getHot(limit = 8): Title[] {
  return [...allTitles].sort((a, b) => b.viewsNum - a.viewsNum).slice(0, limit);
}

export function getRelated(target: Title, limit = 6): Title[] {
  const scored = allTitles
    .filter((item) => item.id !== target.id)
    .map((item) => {
      const genreScore = item.genres.filter((g) => target.genres.includes(g)).length * 3;
      const typeScore = item.type === target.type ? 2 : 0;
      const regionScore = item.region === target.region ? 1 : 0;
      return { item, score: genreScore + typeScore + regionScore };
    })
    .sort((a, b) => b.score - a.score || b.item.viewsNum - a.item.viewsNum);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function filterTitles(type: MediaType, filter: FilterState): Title[] {
  let list = getTitlesByType(type);
  if (filter.region !== "全部") list = list.filter((item) => item.region === filter.region);
  if (filter.year !== "全部") list = list.filter((item) => String(item.year) === filter.year);
  if (filter.genre !== "全部") list = list.filter((item) => item.genres.includes(filter.genre));

  const sorted = [...list];
  if (filter.sort === "latest") sorted.sort((a, b) => b.year - a.year || b.viewsNum - a.viewsNum);
  else if (filter.sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
  else sorted.sort((a, b) => b.viewsNum - a.viewsNum);
  return sorted;
}

export function searchTitles(keyword: string): Title[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allTitles
    .filter((item) => {
      const haystack = [
        item.title,
        item.region,
        item.director,
        ...item.genres,
        ...item.actors,
        ...item.tags,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    })
    .sort((a, b) => b.viewsNum - a.viewsNum);
}

export const NEWS_CATEGORIES = ["全部", "影视新闻", "行业动态", "影评", "预告片"];

export function getLatestNews(limit = 3) {
  return [...news].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}

export function getAllNews() {
  return [...news].sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function getNewsById(id: string) {
  return news.find((item) => item.id === id);
}

export function getRelatedNews(id: string, category: string, limit = 3) {
  const pool = news.filter((item) => item.id !== id);
  const sameCategory = pool.filter((item) => item.category === category);
  const others = pool.filter((item) => item.category !== category);
  return [...sameCategory, ...others].slice(0, limit);
}

export function formatDate(date: string): string {
  return date.replace(/-/g, " / ");
}